import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Wrench, RotateCcw, Settings, FileText, HardHat, Users,
  Calculator, CheckCircle, HelpCircle, ArrowRight, Phone,
  Shield, Clock, Percent, Euro, ChevronDown, ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Footer from '../components/Footer';

const COMMISSION_PILIERS = [
  {
    id: 'vente',
    icon: Wrench,
    title: 'Vente de Matériel',
    color: 'from-blue-500 to-blue-600',
    description: 'Commission dégressive selon le prix de vente',
    grille: [
      { tranche: 'Jusqu\'à 10 000 €', taux: '12%', minimum: '1 200 €' },
      { tranche: '10 001 € - 50 000 €', taux: '10%', minimum: '1 500 €' },
      { tranche: '50 001 € - 150 000 €', taux: '8%', minimum: '5 000 €' },
      { tranche: '150 001 € - 500 000 €', taux: '6%', minimum: '12 000 €' },
      { tranche: 'Plus de 500 000 €', taux: '4%', minimum: '30 000 €' },
    ],
    exemple: 'Pour une foreuse vendue 80 000 € → Commission : 6 400 € (8%)',
    cta: '/vendre',
    ctaLabel: 'Vendre mon matériel'
  },
  {
    id: 'location',
    icon: RotateCcw,
    title: 'Location de Matériel',
    color: 'from-purple-500 to-purple-600',
    description: 'Frais fixe + commission mensuelle plafonnée',
    grille: [
      { tranche: 'Frais de mise en relation', taux: '100 € à 300 €', minimum: 'selon matériel' },
      { tranche: 'Commission mensuelle', taux: '5%', minimum: 'du loyer' },
      { tranche: 'Plafond par opération', taux: '500 € max', minimum: '' },
    ],
    exemple: 'Location 2 000 €/mois pendant 3 mois → Frais: 200€ + 300€ (5%) = 500€ total',
    cta: '/louer-mon-materiel',
    ctaLabel: 'Proposer ma location'
  },
  {
    id: 'service',
    icon: Settings,
    title: 'Services Techniques',
    color: 'from-green-500 to-green-600',
    description: 'Commission sur prestation + frais de mise en relation',
    grille: [
      { tranche: 'Frais de mise en relation', taux: '100 €', minimum: 'fixe' },
      { tranche: 'Commission sur prestation', taux: '10%', minimum: 'du montant HT' },
    ],
    exemple: 'Maintenance 3 000 € → Frais: 100€ + 300€ (10%) = 400€',
    cta: '/service-technique',
    ctaLabel: 'Trouver un prestataire'
  },
  {
    id: 'etude',
    icon: FileText,
    title: 'Études Géotechniques',
    color: 'from-cyan-500 to-cyan-600',
    description: 'Commission sur mission + frais de qualification',
    grille: [
      { tranche: 'Frais de qualification', taux: '200 €', minimum: 'fixe' },
      { tranche: 'Commission sur étude', taux: '8%', minimum: 'du montant HT' },
    ],
    exemple: 'Mission G2 à 15 000 € → Frais: 200€ + 1 200€ (8%) = 1 400€',
    cta: '/etude-geotechnique',
    ctaLabel: 'Demander une étude'
  },
  {
    id: 'travaux',
    icon: HardHat,
    title: 'Travaux de Fondations',
    color: 'from-amber-500 to-amber-600',
    description: 'Commission dégressive avec plafond',
    grille: [
      { tranche: 'Frais de qualification', taux: '500 €', minimum: 'fixe' },
      { tranche: 'Travaux < 100 000 €', taux: '5%', minimum: '' },
      { tranche: 'Travaux 100k - 500k €', taux: '4%', minimum: '' },
      { tranche: 'Travaux > 500 000 €', taux: '3%', minimum: '' },
      { tranche: 'Plafond par opération', taux: '30 000 € max', minimum: '' },
    ],
    exemple: 'Chantier micropieux 200 000 € → Frais: 500€ + 8 000€ (4%) = 8 500€',
    cta: '/travaux-fondations',
    ctaLabel: 'Demander un devis'
  },
  {
    id: 'recrutement',
    icon: Users,
    title: 'Personnel qualifié géotech',
    color: 'from-rose-500 to-rose-600',
    description: 'Forfait selon le type de poste',
    grille: [
      { tranche: 'Opérateur / Technicien', taux: '1 500 €', minimum: 'forfait' },
      { tranche: 'Chef de chantier', taux: '2 500 €', minimum: 'forfait' },
      { tranche: 'Ingénieur / Cadre', taux: '4 000 €', minimum: 'forfait' },
      { tranche: 'Directeur / Manager', taux: '5 000 €', minimum: 'forfait' },
      { tranche: 'Garantie remplacement', taux: '3 mois', minimum: 'inclus' },
    ],
    exemple: 'Placement opérateur foreuse → Forfait: 1 500€ (payé à l\'embauche)',
    cta: '/recrutement',
    ctaLabel: 'Trouver du personnel'
  },
];

const FAQ_ITEMS = [
  {
    question: "Quand est-ce que je paye ?",
    answer: "Vous ne payez QUE si on délivre. Pas de frais d'inscription, pas d'abonnement obligatoire. La commission est prélevée uniquement lorsque la transaction est conclue (vente finalisée, location signée, prestation réalisée, embauche confirmée)."
  },
  {
    question: "Et si ça ne marche pas ?",
    answer: "Aucun frais. Notre modèle est 100% basé sur le résultat. Si nous ne trouvons pas d'acheteur, de locataire, ou de candidat, vous ne payez rien."
  },
  {
    question: "Comment gérez-vous les visites et inspections ?",
    answer: "Notre équipe se déplace sur site pour photographier le matériel, vérifier son état, et gérer les visites des acheteurs potentiels. Tout est inclus dans notre commission."
  },
  {
    question: "Les prix sont-ils négociables ?",
    answer: "Pour les gros volumes ou les partenariats récurrents, nous proposons des conditions préférentielles. Contactez-nous pour en discuter."
  },
  {
    question: "Quelle est la durée d'un mandat de vente ?",
    answer: "Nos mandats sont généralement de 6 mois, renouvelables. Vous pouvez résilier à tout moment avec un préavis de 30 jours si vous n'êtes pas satisfait."
  },
  {
    question: "Comment sont sécurisés les paiements ?",
    answer: "Nous utilisons des comptes séquestres pour les transactions importantes. L'argent n'est libéré qu'après validation de la livraison et inspection du matériel."
  },
];

export default function Commissions() {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [calculatorValue, setCalculatorValue] = useState(50000);
  const [calculatorType, setCalculatorType] = useState('vente');

  const calculateCommission = () => {
    if (calculatorType === 'vente') {
      if (calculatorValue <= 10000) return Math.max(calculatorValue * 0.12, 1200);
      if (calculatorValue <= 50000) return Math.max(calculatorValue * 0.10, 1500);
      if (calculatorValue <= 150000) return Math.max(calculatorValue * 0.08, 5000);
      if (calculatorValue <= 500000) return Math.max(calculatorValue * 0.06, 12000);
      return Math.max(calculatorValue * 0.04, 30000);
    }
    if (calculatorType === 'location') {
      return Math.min(200 + calculatorValue * 0.05, 500);
    }
    return calculatorValue * 0.08;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-white/20 text-white mb-4">
            <Shield className="w-4 h-4 mr-2" />
            Transparence totale
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Notre rémunération en toute transparence
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Pas d'abonnement obligatoire. Pas de frais cachés.<br/>
            <span className="text-white font-semibold">Vous ne payez QUE si on délivre.</span>
          </p>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 mt-8">
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>0€ à l'inscription</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <Clock className="w-5 h-5 text-blue-400" />
              <span>Paiement au résultat</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <Percent className="w-5 h-5 text-amber-400" />
              <span>Commissions dégressives</span>
            </div>
          </div>
        </div>
      </section>

      {/* Commission Cards */}
      <section className="py-16 -mt-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {COMMISSION_PILIERS.map((pilier) => {
              const Icon = pilier.icon;
              return (
                <Card key={pilier.id} className="overflow-hidden hover:shadow-xl transition-all duration-300">
                  <div className={`bg-gradient-to-r ${pilier.color} p-4 text-white`}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{pilier.title}</h3>
                        <p className="text-sm text-white/80">{pilier.description}</p>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    {/* Grille tarifaire */}
                    <div className="space-y-2 mb-4">
                      {pilier.grille.map((ligne, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm py-1 border-b border-gray-100 last:border-0">
                          <span className="text-gray-600">{ligne.tranche}</span>
                          <div className="text-right">
                            <span className="font-semibold text-[#0A2540]">{ligne.taux}</span>
                            {ligne.minimum && <span className="text-xs text-gray-400 ml-1">({ligne.minimum})</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Exemple */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <p className="text-xs text-gray-500 mb-1">Exemple :</p>
                      <p className="text-sm text-[#0A2540]">{pilier.exemple}</p>
                    </div>
                    
                    {/* CTA */}
                    <Link to={pilier.cta}>
                      <Button className="w-full bg-[#635BFF] hover:bg-[#635BFF]/90">
                        {pilier.ctaLabel}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <Calculator className="w-12 h-12 text-[#635BFF] mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">Calculateur de Commission</h2>
            <p className="text-gray-600 mb-8">Estimez le coût de notre service en quelques clics</p>
            
            <Card className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type de service</label>
                  <select
                    value={calculatorType}
                    onChange={(e) => setCalculatorType(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg"
                  >
                    <option value="vente">Vente de matériel</option>
                    <option value="location">Location (loyer mensuel)</option>
                    <option value="etude">Étude géotechnique</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant (€ HT)
                  </label>
                  <input
                    type="number"
                    value={calculatorValue}
                    onChange={(e) => setCalculatorValue(parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-3 border rounded-lg"
                    placeholder="Ex: 50000"
                  />
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-[#635BFF] to-[#8B85FF] rounded-xl p-6 text-white">
                <p className="text-sm opacity-80 mb-2">Commission estimée</p>
                <p className="text-4xl font-bold">{calculateCommission().toLocaleString()} €</p>
                <p className="text-sm opacity-80 mt-2">
                  Soit {((calculateCommission() / calculatorValue) * 100).toFixed(1)}% du montant
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#0A2540] text-center mb-12">
            Pourquoi choisir GEOTS ?
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-3 gap-4 mb-4 text-center font-semibold">
              <div></div>
              <div className="bg-[#635BFF] text-white rounded-t-lg py-3">GEOTS</div>
              <div className="bg-gray-200 rounded-t-lg py-3">Vendre seul</div>
            </div>
            
            {[
              { label: 'Frais d\'inscription', geots: '0€', seul: '0€' },
              { label: 'Visibilité', geots: 'Réseau pro + LinkedIn', seul: 'Limitée' },
              { label: 'Gestion des visites', geots: 'Incluse', seul: 'À votre charge' },
              { label: 'Photos professionnelles', geots: 'Incluses', seul: 'À votre charge' },
              { label: 'Négociation', geots: 'Gérée par experts', seul: 'Seul face à l\'acheteur' },
              { label: 'Sécurisation paiement', geots: 'Séquestre', seul: 'Risque de fraude' },
              { label: 'Temps investi', geots: '~0h', seul: '20-50h' },
            ].map((row, idx) => (
              <div key={idx} className="grid grid-cols-3 gap-4 py-3 border-b">
                <div className="text-gray-700">{row.label}</div>
                <div className="text-center font-medium text-green-600">{row.geots}</div>
                <div className="text-center text-gray-500">{row.seul}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#0A2540] text-center mb-12">
            Questions fréquentes
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {FAQ_ITEMS.map((item, idx) => (
              <Card 
                key={idx} 
                className="cursor-pointer hover:shadow-md transition-all"
                onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <HelpCircle className="w-5 h-5 text-[#635BFF]" />
                      <span className="font-medium text-[#0A2540]">{item.question}</span>
                    </div>
                    {expandedFaq === idx ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  {expandedFaq === idx && (
                    <p className="mt-4 text-gray-600 pl-8">{item.answer}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#635BFF] to-[#8B85FF]">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Prêt à nous confier votre projet ?</h2>
          <p className="text-xl opacity-90 mb-8">
            Contactez-nous pour discuter de vos besoins spécifiques
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/vendre">
              <Button size="lg" className="bg-white text-[#635BFF] hover:bg-gray-100">
                <Wrench className="w-5 h-5 mr-2" />
                Vendre mon matériel
              </Button>
            </Link>
            <a href="tel:+33782939383">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Phone className="w-5 h-5 mr-2" />
                +33 7 82 93 93 83
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
